import { useState, useEffect, useRef } from "react";
import { Volume2, VolumeX, Play, Pause, Film, Upload, Link as LinkIcon, Loader2, Trash2, Image as ImageIcon } from "lucide-react";
import api, { fileUrl, apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { HERO } from "../lib/constants";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { toast } from "sonner";

const CHUNK = 3 * 1024 * 1024; // 3MB

// Warm curated still frames admins can use as the video poster.
const POSTERS = [
  "https://images.unsplash.com/photo-1587571065775-0d2ae0c863e0?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80",
  "https://images.unsplash.com/photo-1758599670047-628d6b0b01e3?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80",
  "https://images.unsplash.com/photo-1777454317637-eebb4379d2f9?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80",
  "https://images.unsplash.com/photo-1608463026422-8f43ab4ebac0?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80",
];

export default function HeroVideo() {
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [muted, setMuted] = useState(false);
  const [playing, setPlaying] = useState(true);
  const [manageOpen, setManageOpen] = useState(false);
  const videoRef = useRef();

  const load = () => api.get("/settings").then((r) => setSettings(r.data)).catch(() => setSettings({}));
  useEffect(() => { load(); }, []);

  const isAdmin = user?.role === "admin";
  const hasVideo = settings?.hero_video_url;

  // Try to autoplay WITH sound. Browsers block unmuted autoplay on first load,
  // so if that's rejected we fall back to muted autoplay + a "Tap for sound" prompt.
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !hasVideo) return;
    v.volume = 1;
    v.muted = false;
    setMuted(false);
    const p = v.play();
    if (p && p.catch) {
      p.catch(() => {
        v.muted = true;
        setMuted(true);
        v.play().catch(() => {});
      });
    }
  }, [hasVideo, settings?.hero_video_url]);

  // When a new visitor accepts the welcome/community-rules gate, that click is a
  // real user gesture — restart the hero from the top WITH sound for full effect.
  useEffect(() => {
    const onAgreed = () => {
      const v = videoRef.current;
      if (!v) return;
      v.muted = false;
      v.volume = 1;
      setMuted(false);
      try { v.currentTime = 0; } catch { /* ignore */ }
      setPlaying(true);
      v.play().catch(() => {});
    };
    window.addEventListener("haplap:agreed", onAgreed);
    return () => window.removeEventListener("haplap:agreed", onAgreed);
  }, []);

  const toggleMute = () => { const v = videoRef.current; if (!v) return; v.muted = !v.muted; setMuted(v.muted); if (!v.muted) v.play().catch(() => {}); };
  const togglePlay = () => { const v = videoRef.current; if (!v) return; if (v.paused) { v.play(); setPlaying(true); } else { v.pause(); setPlaying(false); } };

  if (settings === null) return <div className="h-[40vh] flex items-center justify-center text-copper font-serif">Loading…</div>;

  // No video: admins see an upload prompt; visitors just skip (Landing shows its normal hero).
  if (!hasVideo) {
    if (!isAdmin) return null;
    return (
      <>
        <section data-testid="hero-video-empty" className="relative w-full">
          <div className="relative h-[46vh] min-h-[320px] overflow-hidden">
            <img src={HERO} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-bronze/60 flex flex-col items-center justify-center text-center px-6">
              <Film className="w-10 h-10 text-white/90" />
              <h2 className="font-serif text-2xl sm:text-3xl font-bold text-white mt-3">Add your explainer video</h2>
              <p className="text-white/80 max-w-md mt-2 text-sm">This is the first thing visitors will see. Upload the heartfelt video you made — it'll autoplay full-width right here.</p>
              <Button data-testid="add-hero-video-btn" onClick={() => setManageOpen(true)} className="mt-4 rounded-full bg-white text-bronze hover:bg-white/90">
                <Upload className="w-4 h-4 mr-1.5" /> Upload hero video
              </Button>
            </div>
          </div>
        </section>
        <HeroManager open={manageOpen} onClose={() => setManageOpen(false)} settings={settings} onSaved={load} />
      </>
    );
  }

  return (
    <>
      <section data-testid="hero-video-section" className="relative w-full bg-black">
        <div className="relative h-[62vh] min-h-[380px] max-h-[760px] overflow-hidden">
          <video
            ref={videoRef}
            data-testid="hero-video"
            src={fileUrl(settings.hero_video_url)}
            poster={fileUrl(settings.hero_poster_url) || undefined}
            autoPlay loop playsInline preload="auto"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/40 to-transparent pointer-events-none" />

          <div className="absolute top-4 right-4 flex gap-2">
            <button data-testid="hero-play-toggle" onClick={togglePlay} className="w-10 h-10 rounded-full bg-black/40 backdrop-blur text-white flex items-center justify-center hover:bg-black/60">
              {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            </button>
            <button data-testid="hero-mute-toggle" onClick={toggleMute} className="w-10 h-10 rounded-full bg-black/40 backdrop-blur text-white flex items-center justify-center hover:bg-black/60">
              {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
            </button>
          </div>

          {muted && (
            <button data-testid="hero-tap-sound" onClick={toggleMute}
              className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 text-sm font-medium bg-white/90 text-bronze px-4 py-2.5 rounded-full shadow-lg hover:bg-white animate-pulse">
              <Volume2 className="w-4 h-4" /> Tap to hear our story
            </button>
          )}

          {isAdmin && (
            <button data-testid="manage-hero-btn" onClick={() => setManageOpen(true)} className="absolute top-4 left-4 text-xs bg-white/15 backdrop-blur text-white px-3 py-1.5 rounded-full hover:bg-white/25 flex items-center gap-1.5">
              <Film className="w-3.5 h-3.5" /> Manage video
            </button>
          )}
        </div>
      </section>

      <div data-testid="hero-wordmark" className="text-center bg-cream py-6 sm:py-8 px-4 border-b border-border">
        <h1 className="font-serif text-4xl sm:text-5xl font-bold tracking-tight text-bronze">HAPLAP</h1>
        <p className="font-serif italic text-copper text-sm sm:text-base mt-1.5">Have a penny, leave a penny. Need a penny, take a penny.</p>
      </div>

      <HeroManager open={manageOpen} onClose={() => setManageOpen(false)} settings={settings} onSaved={load} />
    </>
  );
}

function HeroManager({ open, onClose, settings, onSaved }) {
  const [url, setUrl] = useState("");
  const [headline, setHeadline] = useState(settings?.hero_headline || "");
  const [subtext, setSubtext] = useState(settings?.hero_subtext || "");
  const [progress, setProgress] = useState(null);
  const [saving, setSaving] = useState(false);
  const [posterBusy, setPosterBusy] = useState(false);
  const fileRef = useRef();
  const posterRef = useRef();

  useEffect(() => { setHeadline(settings?.hero_headline || ""); setSubtext(settings?.hero_subtext || ""); setUrl(""); }, [settings, open]);

  const setPoster = async (posterUrl) => {
    setPosterBusy(true);
    try { await api.put("/settings/hero", { hero_poster_url: posterUrl }); toast.success("Poster frame updated 💛"); onSaved(); }
    catch (e) { toast.error(apiErr(e)); }
    setPosterBusy(false);
  };

  const uploadPoster = async (e) => {
    const f = e.target.files[0]; if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Please choose an image file."); e.target.value = ""; return; }
    setPosterBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.put("/settings/hero", { hero_poster_url: data.url });
      toast.success("Poster frame set 💛"); onSaved();
    } catch (err) { toast.error(apiErr(err)); }
    setPosterBusy(false);
    e.target.value = "";
  };

  const removePoster = async () => {
    try { await api.delete("/settings/hero-poster"); toast.success("Poster removed"); onSaved(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const saveMeta = async (extra = {}) => {
    await api.put("/settings/hero", { hero_headline: headline, hero_subtext: subtext, ...extra });
  };

  const uploadFile = async (e) => {
    const f = e.target.files[0]; if (!f) return;
    if (!f.type.startsWith("video/")) { toast.error("Please choose a video file."); return; }
    try {
      setProgress(0);
      const initRes = await (async () => {
        const fd = new FormData(); fd.append("filename", f.name); fd.append("content_type", f.type || "video/mp4");
        const { data } = await api.post("/upload/hero/init", fd, { headers: { "Content-Type": "multipart/form-data" } });
        return data;
      })();
      const total = Math.ceil(f.size / CHUNK);
      for (let i = 0; i < total; i++) {
        const blob = f.slice(i * CHUNK, (i + 1) * CHUNK);
        const fd = new FormData();
        fd.append("upload_id", initRes.upload_id); fd.append("index", i); fd.append("chunk", blob, "chunk");
        await api.post("/upload/hero/chunk", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setProgress(Math.round(((i + 1) / total) * 100));
      }
      const fd = new FormData(); fd.append("upload_id", initRes.upload_id); fd.append("total", total);
      const { data } = await api.post("/upload/hero/complete", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await saveMeta({ hero_video_url: data.url });
      toast.success("Hero video is live 💛");
      setProgress(null); onSaved(); onClose();
    } catch (err) { toast.error(apiErr(err)); setProgress(null); }
    e.target.value = "";
  };

  const saveUrl = async () => {
    if (!url.trim()) { toast.error("Paste a video URL first."); return; }
    setSaving(true);
    try { await saveMeta({ hero_video_url: url.trim() }); toast.success("Hero video updated 💛"); onSaved(); onClose(); }
    catch (e) { toast.error(apiErr(e)); }
    setSaving(false);
  };

  const removeVideo = async () => {
    try { await api.delete("/settings/hero"); toast.success("Video removed"); onSaved(); onClose(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="hero-manager" className="sm:max-w-lg rounded-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-serif text-2xl text-bronze flex items-center gap-2"><Film className="w-5 h-5 text-copper" /> Hero explainer video</DialogTitle><DialogDescription className="sr-only">Upload or link a hero video, choose a poster frame, and edit the headline.</DialogDescription></DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-widest text-copper font-mono">Upload a video</Label>
            <input ref={fileRef} type="file" accept="video/*" hidden onChange={uploadFile} data-testid="hero-file-input" />
            <Button data-testid="hero-upload-btn" onClick={() => fileRef.current?.click()} disabled={progress !== null} variant="outline" className="w-full rounded-xl mt-1">
              {progress !== null ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading… {progress}%</> : <><Upload className="w-4 h-4 mr-2" /> Choose video file</>}
            </Button>
            <p className="text-[11px] text-muted-foreground mt-1">Large files upload in chunks automatically. MP4/WebM recommended.</p>
          </div>

          <div className="relative text-center"><span className="text-xs text-muted-foreground bg-card px-2">or</span><div className="absolute inset-x-0 top-1/2 -z-10 border-t border-border" /></div>

          <div>
            <Label className="text-xs uppercase tracking-widest text-copper font-mono flex items-center gap-1"><LinkIcon className="w-3 h-3" /> Paste a video URL</Label>
            <div className="flex gap-2 mt-1">
              <Input data-testid="hero-url-input" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…/your-video.mp4" className="rounded-xl" />
              <Button data-testid="hero-url-save" onClick={saveUrl} disabled={saving} className="rounded-xl bg-copper hover:bg-copper/90 text-white shrink-0">Set</Button>
            </div>
          </div>

          <div className="border-t border-border pt-4">
            <Label className="text-xs uppercase tracking-widest text-copper font-mono flex items-center gap-1"><ImageIcon className="w-3 h-3" /> Poster frame (shown before the video plays)</Label>
            <p className="text-[11px] text-muted-foreground mt-1">Set a warm still so the first frame is never black while the video loads.</p>

            {settings?.hero_poster_url && (
              <div className="relative mt-3 rounded-2xl overflow-hidden border border-border/60 aspect-video bg-black/5" data-testid="poster-current-preview">
                <img src={fileUrl(settings.hero_poster_url)} alt="current poster" className="w-full h-full object-cover" />
                <button data-testid="poster-remove-btn" onClick={removePoster} className="absolute top-2 right-2 text-[11px] bg-black/50 backdrop-blur text-white px-2.5 py-1 rounded-full hover:bg-black/70 flex items-center gap-1">
                  <Trash2 className="w-3 h-3" /> Remove
                </button>
              </div>
            )}

            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2.5 mt-3" data-testid="poster-frame-grid">
              {POSTERS.map((p, idx) => {
                const active = settings?.hero_poster_url === p;
                return (
                  <button key={p} data-testid={`poster-frame-thumb-${idx}`} onClick={() => setPoster(p)} disabled={posterBusy}
                    className={`aspect-video rounded-xl overflow-hidden border-2 transition-all hover:scale-[1.03] active:scale-95 ${active ? "border-copper ring-2 ring-copper/30 shadow-md" : "border-border/60 hover:border-copper/60 opacity-90 hover:opacity-100"}`}>
                    <img src={p} alt="" className="w-full h-full object-cover" />
                  </button>
                );
              })}
            </div>

            <input ref={posterRef} type="file" accept="image/*" hidden onChange={uploadPoster} data-testid="poster-custom-upload-input" />
            <Button data-testid="poster-upload-btn" onClick={() => posterRef.current?.click()} disabled={posterBusy} variant="outline" className="w-full rounded-xl mt-2.5 min-h-[44px]">
              {posterBusy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Working…</> : <><Upload className="w-4 h-4 mr-2" /> Upload your own image</>}
            </Button>
          </div>

          <div className="border-t border-border pt-4 space-y-3">
            <div><Label>Headline</Label><Input data-testid="hero-headline-input" value={headline} onChange={(e) => setHeadline(e.target.value)} placeholder="Everyone needs a little help sometimes." className="rounded-xl mt-1" /></div>
            <div><Label>Subtext</Label><Input data-testid="hero-subtext-input" value={subtext} onChange={(e) => setSubtext(e.target.value)} placeholder="We all go through things…" className="rounded-xl mt-1" /></div>
            <Button data-testid="hero-meta-save" onClick={async () => { await saveMeta(); toast.success("Saved 💛"); onSaved(); }} variant="outline" className="rounded-full">Save text</Button>
          </div>

          {settings?.hero_video_url && (
            <Button data-testid="hero-remove-btn" onClick={removeVideo} variant="ghost" className="text-destructive rounded-full w-full"><Trash2 className="w-4 h-4 mr-1.5" /> Remove current video</Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
